import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useInfiniteQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  CheckIcon,
  ClipboardListIcon,
  PencilIcon,
  RefreshCwIcon,
  XIcon,
} from 'lucide-react';
import { type Control, Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  ANIMAL_WRITE_ACTIONS,
  type AnimalEditValues,
  animalProceduresQueryOptions,
  type AnimalRow,
  buildEditAnimalInput,
  EDIT_ERROR_FIELDS,
  isWritable,
  toEditValues,
  useEditAnimals,
  type WritableField,
} from '@/services/animal-management';
import {
  OPTION_SET_OBJECT_TYPES,
  type OptionSetAttribute,
} from '@/services/animal-management/api';
import { useToast } from '@/services/toast';

import {
  OptionSelect,
  ORIGIN_OPTIONS,
  type SelectOption,
  useAnimalStatusOptions,
  useKeyedStringMapOptions,
  YES_NO_OPTIONS,
} from './AnimalFieldInputs';
import { ConvertTypeDialog } from './ConvertTypeDialog';
import { LookupCombobox, type LookupKind } from './LookupCombobox';
import { OptionSetSelect } from './OptionSetSelect';

function useSchema() {
  const { t } = useTranslation();
  return useMemo(
    () =>
      z.object({
        name: z.string(),
        tag: z.string(),
        registration: z.string(),
        rfidTag: z.string(),
        breedId: z.string().nullable(),
        ownerId: z.string().nullable(),
        yardId: z.string().nullable(),
        penId: z.string().nullable(),
        birthDate: z.string(),
        weight: z
          .number()
          .int(t('animalManagement.writes.wholeNumber', 'Enter a whole number'))
          .min(
            0,
            t('animalManagement.writes.wholeNumber', 'Enter a whole number'),
          )
          .nullable(),
        status: z.enum(['active', 'inactive']),
        programCategory: z.string(),
        opuProgram: z.string(),
        pregnant: z.enum(['Yes', 'No', '']),
        receivedDate: z.string(),
        shipDate: z.string(),
        origin: z.string(),
        hairSample: z.enum(['Yes', 'No', '']),
        heiferCow: z.string(),
        lastDrugUsedId: z.string().nullable(),
        client: z.string(),
        colorId: z.string().nullable(),
        ocvStatusId: z.string().nullable(),
        recipientStatusId: z.string().nullable(),
        sex: z.string(),
      }) satisfies z.ZodType<AnimalEditValues>,
    [t],
  );
}

type EditControl = Control<AnimalEditValues>;

type FieldProps = {
  control: EditControl;
  name: WritableField;
  label: string;
};

function TextField({ control, name, label }: FieldProps) {
  const id = useId();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={id}>{label}</FieldLabel>
          <Input
            {...field}
            value={(field.value as string | null) ?? ''}
            id={id}
            aria-invalid={fieldState.invalid}
          />
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

function OptionSetField({
  control,
  name,
  label,
  objectTypeCode,
  attribute,
}: FieldProps & { objectTypeCode: number; attribute: OptionSetAttribute }) {
  const id = useId();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={id}>{label}</FieldLabel>
          <OptionSetSelect
            id={id}
            objectTypeCode={objectTypeCode}
            attribute={attribute}
            value={field.value as string | null}
            onChange={field.onChange}
            invalid={fieldState.invalid}
            disabled={field.disabled}
          />
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

function DateField({ control, name, label }: FieldProps) {
  const id = useId();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={id}>{label}</FieldLabel>
          <DatePicker
            id={id}
            className="h-8"
            disabled={field.disabled}
            value={field.value ? parseISO(field.value as string) : undefined}
            onChange={(date) =>
              field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
            }
          />
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

function LookupField({
  control,
  name,
  label,
  kind,
  selectedLabel,
}: FieldProps & { kind: LookupKind; selectedLabel: string }) {
  const id = useId();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={id}>{label}</FieldLabel>
          <LookupCombobox
            id={id}
            kind={kind}
            value={field.value as string | null}
            onChange={field.onChange}
            selectedLabel={selectedLabel}
            placeholder={label}
            invalid={fieldState.invalid}
            disabled={field.disabled}
          />
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

function SelectField({
  control,
  name,
  label,
  options,
  selectedLabel,
  loading,
}: FieldProps & {
  options: SelectOption[];
  selectedLabel?: string;
  loading?: boolean;
}) {
  const id = useId();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid}>
          <FieldLabel htmlFor={id}>{label}</FieldLabel>
          <OptionSelect
            id={id}
            options={options}
            value={field.value as string | null}
            onChange={field.onChange}
            selectedLabel={selectedLabel}
            invalid={fieldState.invalid}
            disabled={field.disabled || loading}
          />
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} value={value} disabled readOnly />
    </Field>
  );
}

type EditAnimalDialogProps = {
  animal: AnimalRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function EditAnimalDialog({
  animal,
  onOpenChange,
  onSuccess,
}: EditAnimalDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const schema = useSchema();
  const editAnimals = useEditAnimals();
  const open = animal !== null;
  const [isEditingEnabled, setIsEditingEnabled] = useState(false);
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);

  const {
    data: procedurePages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(animalProceduresQueryOptions(animal?.id ?? null));
  const procedures = useMemo(
    () => procedurePages?.pages.flatMap((page) => page.items) ?? [],
    [procedurePages],
  );
  const procedureCount = procedurePages?.pages[0]?.totalCount ?? 0;
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) {
        void fetchNextPage();
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const values = useMemo(
    () => (animal ? toEditValues(animal) : undefined),
    [animal],
  );

  const form = useForm<AnimalEditValues>({
    resolver: zodResolver(schema),
    values,
    disabled: !isEditingEnabled || editAnimals.isPending,
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) setIsEditingEnabled(false);
    onOpenChange(next);
  };

  const canEdit =
    ANIMAL_WRITE_ACTIONS.editAnimal &&
    animal !== null &&
    animal.animalType !== 'LOPU Donor';

  const show = (field: WritableField) =>
    animal !== null && isWritable(animal.animalType, field);

  const recipientStatuses = useAnimalStatusOptions('recipient');
  const ocvStatuses = useAnimalStatusOptions('ocv');
  const clients = useKeyedStringMapOptions(
    OPTION_SET_OBJECT_TYPES.recipient,
    'tog_client',
  );
  const sexes = useKeyedStringMapOptions(
    OPTION_SET_OBJECT_TYPES.calf,
    'tog_sex',
  );
  const heiferCows = useKeyedStringMapOptions(
    OPTION_SET_OBJECT_TYPES.donor,
    'tog_heifercow',
  );

  const onSubmit = async (values: AnimalEditValues) => {
    if (!animal) return;
    const { input, cleared } = buildEditAnimalInput(animal, values);
    for (const field of cleared) {
      form.setError(field, {
        type: 'manual',
        message: t(
          'animalManagement.writes.cannotClear',
          "Clearing this field isn't supported yet",
        ),
      });
    }
    if (cleared.length) return;
    if (!input) {
      handleOpenChange(false);
      return;
    }

    const [result] = await editAnimals.mutateAsync([input]);
    if (!result.success) {
      let mapped = false;
      for (const [key, message] of Object.entries(result.errors ?? {})) {
        const field = EDIT_ERROR_FIELDS[key];
        if (field) {
          form.setError(field, { type: 'server', message });
          mapped = true;
        }
      }
      if (!mapped) {
        toast.show({
          severity: 'error',
          summary: t('common.error', 'Error'),
          detail:
            result.message ??
            t(
              'animalManagement.writes.updateFailed',
              'Failed to update animal',
            ),
          life: 8000,
        });
      }
      return;
    }

    toast.show({
      severity: 'success',
      summary: t('common.success', 'Success'),
      detail: t('animalManagement.writes.animalUpdated', 'Animal was updated.'),
    });
    handleOpenChange(false);
    onSuccess();
  };

  const formId = useId();
  const c = form.control;
  const col = (key: string, fallback: string) =>
    t(`animalManagement.columns.${key}`, fallback);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('animalManagement.editAnimal.title', 'Edit Animal')}{' '}
              {animal ? `- ${animal.nameTag}` : ''}
            </DialogTitle>
          </DialogHeader>

          <DialogBody
            as="form"
            id={formId}
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn(
              'flex flex-col gap-6',
              isConvertDialogOpen && 'overflow-hidden',
            )}
          >
            <div className="grid grid-cols-4 gap-x-4 gap-y-3">
              <ReadOnlyField
                label={col('togId', 'TOG ID')}
                value={animal?.togId ?? ''}
              />
              <ReadOnlyField
                label={col('animalType', 'Animal Type')}
                value={animal?.animalType ?? ''}
              />
              {show('name') && (
                <TextField
                  control={c}
                  name="name"
                  label={col('name', 'Name')}
                />
              )}
              {show('tag') && (
                <TextField control={c} name="tag" label={col('tag', 'Tag')} />
              )}
              {show('registration') && (
                <TextField
                  control={c}
                  name="registration"
                  label={t(
                    'animalManagement.registrationNumber',
                    'Registration Number',
                  )}
                />
              )}
              {show('rfidTag') && (
                <TextField
                  control={c}
                  name="rfidTag"
                  label={col('rfidTag', 'RFID Tag')}
                />
              )}
              {show('breedId') && (
                <LookupField
                  control={c}
                  name="breedId"
                  kind="breed"
                  label={col('breed', 'Breed')}
                  selectedLabel={animal?.breed ?? ''}
                />
              )}
              {show('birthDate') && (
                <DateField
                  control={c}
                  name="birthDate"
                  label={col('birthDate', 'Birth Date')}
                />
              )}
              {show('ownerId') && (
                <LookupField
                  control={c}
                  name="ownerId"
                  kind="owner"
                  label={col('ranch', 'Ranch')}
                  selectedLabel={animal?.ranch ?? ''}
                />
              )}
              {show('yardId') && (
                <LookupField
                  control={c}
                  name="yardId"
                  kind="yard"
                  label={col('yard', 'Yard')}
                  selectedLabel={animal?.yard ?? ''}
                />
              )}
              {show('penId') && (
                <LookupField
                  control={c}
                  name="penId"
                  kind="pen"
                  label={col('housing', 'Housing')}
                  selectedLabel={animal?.housing ?? ''}
                />
              )}
              {!show('penId') && animal?.housing && (
                <ReadOnlyField
                  label={col('housing', 'Housing')}
                  value={animal.housing}
                />
              )}
              {show('receivedDate') && (
                <DateField
                  control={c}
                  name="receivedDate"
                  label={col('receivedDate', 'Received Date')}
                />
              )}
              {show('shipDate') && (
                <DateField
                  control={c}
                  name="shipDate"
                  label={col('shipDate', 'Ship Date')}
                />
              )}
              {show('weight') && (
                <Controller
                  name="weight"
                  control={c}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="edit-animal-weight">
                        {col('weight', 'Weight')} (lbs)
                      </FieldLabel>
                      <Input
                        id="edit-animal-weight"
                        type="number"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(
                            Number.isNaN(e.target.valueAsNumber)
                              ? null
                              : e.target.valueAsNumber,
                          )
                        }
                        aria-invalid={fieldState.invalid}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              )}
              {show('origin') && (
                <SelectField
                  control={c}
                  name="origin"
                  label={col('origin', 'Origin')}
                  options={ORIGIN_OPTIONS}
                />
              )}
              {show('colorId') && (
                <LookupField
                  control={c}
                  name="colorId"
                  kind="color"
                  label={col('color', 'Color')}
                  selectedLabel={animal?.color ?? ''}
                />
              )}
              {show('sex') && (
                <SelectField
                  control={c}
                  name="sex"
                  label={col('sex', 'Sex')}
                  options={sexes.options}
                  loading={sexes.isLoading}
                />
              )}
              {show('recipientStatusId') && (
                <SelectField
                  control={c}
                  name="recipientStatusId"
                  label={col('recipientStatus', 'Recip Status')}
                  options={recipientStatuses.options}
                  selectedLabel={animal?.recipientStatus}
                  loading={recipientStatuses.isLoading}
                />
              )}
              {show('ocvStatusId') && (
                <SelectField
                  control={c}
                  name="ocvStatusId"
                  label={col('ocvStatus', 'OCV Status')}
                  options={ocvStatuses.options}
                  selectedLabel={animal?.ocvStatus}
                  loading={ocvStatuses.isLoading}
                />
              )}
              {show('client') && (
                <SelectField
                  control={c}
                  name="client"
                  label={col('client', 'Original Owner')}
                  options={clients.options}
                  loading={clients.isLoading}
                />
              )}
              {show('lastDrugUsedId') && (
                <LookupField
                  control={c}
                  name="lastDrugUsedId"
                  kind="drug"
                  label={col('lastDrugUsed', 'Last Drug Used')}
                  selectedLabel={animal?.lastDrugUsed ?? ''}
                />
              )}
              {show('heiferCow') && (
                <SelectField
                  control={c}
                  name="heiferCow"
                  label={col('cowHeifer', 'Cow/Heifer')}
                  options={heiferCows.options}
                  loading={heiferCows.isLoading}
                />
              )}
              {show('hairSample') && (
                <SelectField
                  control={c}
                  name="hairSample"
                  label={col('hairSample', 'Hair Sample')}
                  options={YES_NO_OPTIONS}
                />
              )}
              {show('pregnant') && (
                <Controller
                  name="pregnant"
                  control={c}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="edit-animal-pregnant">
                        {col('pregnant', 'Pregnant')}
                      </FieldLabel>
                      <Select {...field}>
                        <SelectTrigger
                          id="edit-animal-pregnant"
                          aria-invalid={fieldState.invalid}
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">
                            {t('common.yes', 'Yes')}
                          </SelectItem>
                          <SelectItem value="No">
                            {t('common.no', 'No')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              )}
              {show('programCategory') && (
                <OptionSetField
                  control={c}
                  name="programCategory"
                  label={col('programCategory', 'Program Category')}
                  objectTypeCode={OPTION_SET_OBJECT_TYPES.donor}
                  attribute="tog_category"
                />
              )}
              {show('opuProgram') && (
                <OptionSetField
                  control={c}
                  name="opuProgram"
                  label={col('opuProgram', 'OPU Program')}
                  objectTypeCode={
                    animal?.animalType === 'Calf'
                      ? OPTION_SET_OBJECT_TYPES.calf
                      : OPTION_SET_OBJECT_TYPES.donor
                  }
                  attribute="tog_opuprogram"
                />
              )}
              {show('status') && (
                <Controller
                  name="status"
                  control={c}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel htmlFor="edit-animal-status">
                        {col('status', 'Status')}
                      </FieldLabel>
                      <Select {...field}>
                        <SelectTrigger
                          id="edit-animal-status"
                          aria-invalid={fieldState.invalid}
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">
                            {t('common.active', 'Active')}
                          </SelectItem>
                          <SelectItem value="inactive">
                            {t('common.inactive', 'Inactive')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              )}
            </div>

            <Separator />

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ClipboardListIcon className="size-4" />
                {t(
                  'animalManagement.editAnimal.relatedProcedures',
                  'Related Procedures',
                )}{' '}
                ({procedureCount})
              </div>

              <div className="flex flex-col gap-2">
                {procedures.map((proc) => (
                  <div
                    key={proc.id}
                    className="rounded-lg border border-blue-200 bg-blue-50 p-3"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">
                        {proc.procedureName}
                      </span>
                      {proc.procedureCode && (
                        <span className="rounded bg-blue-200 px-2 py-0.5 font-medium text-blue-800">
                          {proc.procedureCode}
                        </span>
                      )}
                      <span className="text-muted-foreground ml-auto text-sm">
                        {proc.procedureDate.slice(0, 10)}
                      </span>
                    </div>
                    {proc.description && (
                      <p className="text-muted-foreground mt-1 text-sm">
                        {proc.description}
                      </p>
                    )}
                    {proc.performedByName && (
                      <p className="text-muted-foreground text-sm">
                        {col('performedBy', 'Performed By')}:{' '}
                        {proc.performedByName}
                      </p>
                    )}
                  </div>
                ))}
                {hasNextPage && <div ref={loadMoreRef} className="h-1" />}
              </div>
            </div>
          </DialogBody>

          <DialogFooter className="sm:justify-between">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditingEnabled(true)}
                disabled={!canEdit || isEditingEnabled}
              >
                <PencilIcon className="mr-2 size-4" />
                {t('common.edit', 'Edit')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsConvertDialogOpen(true)}
                disabled={!ANIMAL_WRITE_ACTIONS.convertType}
              >
                <RefreshCwIcon className="mr-2 size-4" />
                {t('animalManagement.convertAnimalType.button', 'Convert Type')}
              </Button>
            </div>

            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                <XIcon className="mr-2 size-4" />
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  !form.formState.isDirty ||
                  !isEditingEnabled ||
                  editAnimals.isPending
                }
                form={formId}
              >
                <CheckIcon className="mr-2 size-4" />
                {editAnimals.isPending
                  ? t('common.saving', 'Saving...')
                  : t('common.save', 'Save')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {animal && (
        <ConvertTypeDialog
          open={isConvertDialogOpen}
          onOpenChange={setIsConvertDialogOpen}
          animal={animal}
          // Conversion moves the row to another legacy table; the API keeps
          // it at 501 until that is designed, so the entry point is disabled.
          onConvert={() => setIsConvertDialogOpen(false)}
        />
      )}
    </>
  );
}
